import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import * as dayjs from 'dayjs';
import flatten from 'lodash-es/flatten';
import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../../../app.models';
import * as fromRouter from '../../../router';
import { clearEmptyValue } from '../../../utils';
import { FilterControl, FilterControlKeys, FilterGroup, FilterType, SelectedFilter } from '../../shared.models';
import { Im2mChip } from '@m2mgate/sage-utils';

type Im2mChipExtended = Im2mChip & { paramsKey: fromRouter.ParamsKey };
@Component({
    selector: 'liebherr-table-controls',
    templateUrl: './table-controls.component.html',
    styleUrls: ['./table-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableControlsComponent extends SubscriptionComponent implements OnChanges, AfterViewInit {
    @Input() title?: string;
    @Input() filterGroups: FilterGroup[] = [];
    @Input() filterControlWidth?: string;
    filterExpanded = false;

    filterFormList = this.fb.array([]);

    filterParams$ = this.store.select(fromRouter.selectFilterParams);

    selectedFilter$: Observable<Im2mChipExtended[]>;

    PARAM_KEYS = fromRouter.ParamsKey;
    TYPE = FilterType;

    filterGroups$ = new BehaviorSubject<FilterGroup[]>([]);

    constructor(private fb: UntypedFormBuilder, private store: Store<AppState>, private translateService: TranslocoService) {
        super();

        this.selectedFilter$ = combineLatest([this.filterParams$, this.translateService.selectTranslate('')]).pipe(
            map(([params, temp]) => {
                const selectedFilter: Im2mChipExtended[] = [];

                const filterControls = flatten(this.filterGroups.map((g) => g.controls));

                Object.keys(params).forEach((key) => {
                    const match = filterControls.find((ctrl) => ctrl?.paramsKey === key);

                    if (match && params[key]) {
                        let value = key === fromRouter.ParamsKey.FW_RELEASED_AT ? dayjs(params[key]).toDate() : params[key];
                        if (match.type === FilterType.SELECTION) {
                            const optionMatch = (match.options || []).find((o) => o.value === params[key]);

                            if (optionMatch) {
                                value = this.translateService.translate(optionMatch.label);
                            }
                        }

                        const transLabel = translateService.translate(match.label);
                        const transValue = translateService.translate(value);
                        console.log(transLabel + ': ' + transValue, match.label + ': ' + value);
                        selectedFilter.push({
                            key: transLabel,
                            value: transLabel + ': ' + transValue,
                            paramsKey: key as fromRouter.ParamsKey,
                            removeable: true,
                        });
                    }
                });
                return selectedFilter;
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.filterGroups) {
            this.filterGroups$.next(this.filterGroups);
        }
    }

    ngAfterViewInit() {
        this.sub.add(
            this.filterGroups$
                .pipe(distinctUntilChanged(isEqual), withLatestFrom(this.filterParams$))
                .subscribe(([groups, filterParams]) => {
                    this.updateFilterForm(filterParams);
                })
        );
        this.sub.add(
            this.filterFormList.valueChanges
                .pipe(distinctUntilChanged(isEqual), withLatestFrom(this.filterParams$), debounceTime(500))
                .subscribe(([change, filterParams]) => {
                    const queryParams = this.mapValuesToQueryParams(Array.isArray(change) ? change : [change]);
                    // important: set page:0 to display filter results
                    this.store.dispatch(
                        fromRouter.navigate({
                            url: [],
                            queryParams: { ...queryParams, page: 0 },
                        })
                    );
                })
        );

        this.sub.add(this.filterParams$.pipe(distinctUntilChanged(isEqual)).subscribe((params) => this.mapFilterParamsToValues(params)));
    }

    toggle() {
        this.filterExpanded = !this.filterExpanded;
    }

    clearFilter(input: Im2mChip) {
        const filter: Partial<SelectedFilter> = { ...input };
        const filterControls = flatten(
            this.filterFormList.controls.map((c) => (c.get('filterControls') as UntypedFormArray)?.controls || [])
        ).filter((c) => c);
        const match = filterControls.find((ctrl) => ctrl.get('paramsKey')?.value === filter?.paramsKey);
        match?.get('value')?.patchValue(undefined);
    }

    private updateFilterForm(filterParams: fromRouter.FilterParams) {
        this.filterGroups.forEach((filterGroup) => {
            const group = this.fb.group({
                title: [filterGroup.title],
                filterControls: this.fb.array([]),
            });

            filterGroup.controls.forEach((filterControl) => {
                const ctrlGroup = this.fb.group({
                    [FilterControlKeys.value]: [filterParams[filterControl?.paramsKey]],
                });
                Object.keys(filterControl).forEach((key) => {
                    ctrlGroup.addControl(key, this.fb.control(filterControl[key]));
                });
                (group.get('filterControls') as UntypedFormArray)?.push(ctrlGroup);
            });
            const idx = this.filterFormList.controls.findIndex((ctrl) => ctrl.get('title')?.value === filterGroup.title);

            if (idx > -1) {
                this.filterFormList.removeAt(idx);
                this.filterFormList.insert(idx, group);
            } else {
                this.filterFormList.push(group);
            }
        });
    }

    private mapValuesToQueryParams(change: { title?: string; filterControls: FilterControl[] }[]) {
        const queryParams: fromRouter.FilterParamsMap = {};

        change.forEach((filterGroup) => {
            filterGroup.filterControls.forEach((filterControl) => {
                if (filterControl.type === FilterType.DATE_PICKER) {
                    queryParams[filterControl?.paramsKey] = !isNil(filterControl.value)
                        ? dayjs(filterControl.value).format('YYYY-MM-DD')
                        : undefined;
                } else {
                    queryParams[filterControl?.paramsKey] = clearEmptyValue(filterControl.value);
                }
            });
        });

        return queryParams;
    }
    private mapFilterParamsToValues(filterParams: fromRouter.FilterParams) {
        this.filterFormList.controls.forEach((filterGroup) => {
            const filterControls = filterGroup.get('filterControls') as UntypedFormArray;

            filterControls?.controls.forEach((filterControl) => {
                const key = filterControl.get(FilterControlKeys?.paramsKey)?.value;

                if (key) {
                    filterControl.get(FilterControlKeys.value)?.patchValue(filterParams[key]);
                }
            });
        });
    }
}
